import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import SafeImage from "./SafeImage";
import { prefetchReadyMade } from "../lib/prefetchOnHover";
import { useReadyMadeSale, applyReadyMadeSale } from "../hooks/useReadyMadeSale";

// Compact 5-star row driven by denormalized `review_avg` / `review_count`
// on the product doc (populated server-side in iter47e). We render half-
// stars by overlaying a clipped filled star — keeps the markup cheap on
// grid pages that may show 20+ cards at once.
function StarsInline({ avg, count }) {
  if (!count) return null;
  const rating = Math.max(0, Math.min(5, Number(avg) || 0));
  return (
    <div className="flex items-center gap-1 mt-1" data-testid="rm-card-stars" aria-label={`${rating.toFixed(1)} out of 5 stars from ${count} reviews`}>
      <div className="relative inline-flex">
        <div className="flex">
          {[0,1,2,3,4].map((i) => (
            <Star key={i} size={11} className="text-zinc-700" strokeWidth={1.5} />
          ))}
        </div>
        <div className="absolute inset-0 overflow-hidden flex" style={{ width: `${(rating / 5) * 100}%` }}>
          {[0,1,2,3,4].map((i) => (
            <Star key={i} size={11} className="text-amber-400 fill-amber-400 flex-shrink-0" strokeWidth={1.5} />
          ))}
        </div>
      </div>
      <span className="font-mono text-[10px] tracking-widest text-zinc-500">({count})</span>
    </div>
  );
}

export default function ReadyMadeCard({ product }) {
  // Prefer slug for SEO-friendly URLs (e.g. /design/marvel-comics-collage)
  // — backend accepts both id and slug on GET /api/ready-made/{rm_id}.
  // Sitemap emits slug URLs too (iter47l), keeping canonical signals
  // consistent across crawl + click sources.
  const href = `/design/${product.slug || product.id}`;
  // iter73 — Ready-Made global sale slashed pricing on the grid card.
  const sale = useReadyMadeSale();
  // Base "From $X" is the min enabled garment price; if the admin never
  // set per-garment prices we fall back to the default $35 (t-shirt).
  const gPrices = product.garment_prices || {};
  const enabledPrices = (product.garment_types || ["tshirt", "sweatshirt", "hoodie"])
    .map((g) => Number(gPrices[g]))
    .filter((n) => n > 0);
  const baseFrom = enabledPrices.length ? Math.min(...enabledPrices) : 35;
  const { discounted, showSlash } = applyReadyMadeSale(baseFrom, sale);
  const displayFrom = showSlash ? discounted : baseFrom;
  return (
    <Link
      to={href}
      data-testid={`rm-card-${product.id}`}
      onMouseEnter={() => prefetchReadyMade(product.id)}
      onTouchStart={() => prefetchReadyMade(product.id)}
      className="surface-card group block relative"
    >
      <div className="aspect-[4/5] bg-[#080808] overflow-hidden relative">
        <SafeImage src={product.image_front} alt={product.name} optimize width={500} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute top-3 left-3 flex flex-col gap-1 items-start">
          {showSlash && (
            <span
              className="font-mono text-[10px] tracking-widest px-2 py-1 bg-emerald-500 text-black uppercase"
              data-testid="rm-card-sale-badge"
            >
              {sale.pct}% OFF
            </span>
          )}
          {(product.badges || []).includes("trending") && <span className="badge-trending">TRENDING</span>}
          {(product.badges || []).includes("new-drop") && <span className="badge-newdrop">NEW DROP</span>}
          {product.bestseller_rank && (
            <span className="badge-bestseller" data-testid={`rm-bestseller-auto-${product.id}`}>🔥 BEST SELLER</span>
          )}
        </div>
        {product.stock_status === "out_of_stock" && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center" data-testid="oos-overlay">
            <span className="font-display text-xl text-white border-2 border-[var(--tc-red)] px-4 py-2">OUT OF STOCK</span>
          </div>
        )}
        {product.stock_status === "low_stock" && (
          <div className="absolute top-3 right-3" data-testid="low-stock-badge">
            <span className="badge-trending" style={{ background: "#b45309" }}>LOW STOCK</span>
          </div>
        )}
        <div className="absolute bottom-3 right-3 flex gap-1">
          {(product.colors || []).slice(0, 4).map((c) => (
            <span key={c} className="w-3.5 h-3.5 border border-white/40" style={{ background: c }} />
          ))}
        </div>
      </div>
      <div className="p-4 flex justify-between items-center">
        <div className="min-w-0">
          <div className="font-ui font-bold uppercase tracking-wider text-xs truncate">{product.name}</div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1 capitalize">
            {product.category?.replace("-", " ")}
          </div>
          <StarsInline avg={product.review_avg} count={product.review_count} />
        </div>
        <div className="text-right flex-shrink-0" data-testid="rm-card-price">
          <div className="font-mono font-bold text-[var(--tc-red)] text-sm whitespace-nowrap">
            From&nbsp;${displayFrom.toFixed(2)}
          </div>
          {showSlash && (
            <div
              className="font-mono text-[10px] text-zinc-500 line-through whitespace-nowrap"
              data-testid="rm-card-price-was"
            >
              ${baseFrom.toFixed(2)}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
